import { Editor } from '@tiptap/core';
import { Level } from '@tiptap/extension-heading';
import { setDefaultFontStyle } from './utilityFunctions';
import { LoreeInteractiveEditorDashboardContentType } from '../editorUtilityFunctions/lintEditorType';

interface NodeUpdate {
    pos: number;
    nodeType: string;
    attrs: any;
    nodeFrom: number;
    nodeTo: number;
}

const DEFAULT_STYLES = {
    font: 'SourceSansPro-Regular',
    size: '16px',
    fontStyle: 'normal',
};

function getAdminDashboardStyles(
    editorConfig: LoreeInteractiveEditorDashboardContentType | undefined,
    level: Level | 0
) {
    if (!editorConfig) return DEFAULT_STYLES;
    return setDefaultFontStyle(editorConfig.customHeaderStyleList, level);
}

function getNodeStyleLevel(node: any): { level: Level | 0 } {
    if (node.type.name === 'paragraph') return { level: 0 };
    if (node.type.name === 'heading') return { level: node.attrs.level as Level };

    if (node.type.name === 'listItem') {
        let childNodeType = 'paragraph';
        let headingLevel: Level | null = null;

        node.forEach((child: any) => {
            if (child.type.name === 'heading') {
                childNodeType = 'heading';
                headingLevel = child.attrs.level as Level;
            } else if (child.type.name === 'paragraph') {
                childNodeType = 'paragraph';
            }
        });

        if (childNodeType === 'heading' && headingLevel !== null) {
            return { level: headingLevel };
        }
    }

    return { level: 0 };
}

export function clearContentFormatInsideEditor(
    editor: Editor,
    editorConfig?: LoreeInteractiveEditorDashboardContentType
) {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const { state } = editor;

    const nodesToUpdate: NodeUpdate[] = [];

    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node || !node.type) return true;

        const nodeType = node.type.name;
        if (!['paragraph', 'heading', 'listItem'].includes(nodeType)) return true;

        const nodeFrom = Math.max(pos, from);
        const nodeTo = Math.min(pos + node.nodeSize, to);

        const { level } = getNodeStyleLevel(node);
        const adminDashboardStyles = getAdminDashboardStyles(editorConfig, level);

        if (adminDashboardStyles) {
            const fontFamily = adminDashboardStyles.font || 'SourceSansPro-Regular';

            nodesToUpdate.push({
                pos,
                nodeType,
                nodeFrom,
                nodeTo,
                attrs: {
                    fontFamily,
                    fontSize: adminDashboardStyles.size,
                    color: '#212529',
                    ...(adminDashboardStyles.fontStyle && { fontStyle: adminDashboardStyles.fontStyle }),
                },
            });
        }

        return true;
    });

    let chain = editor.chain().focus().setTextSelection({ from, to }).unsetAllMarks();

    nodesToUpdate.forEach(({ nodeType, attrs, nodeFrom, nodeTo }) => {
        chain = chain.updateNodeStylesInRange(
            [nodeType],
            {
                fontFamily: attrs.fontFamily,
                fontSize: attrs.fontSize,
                color: attrs.color,
                ...(attrs.fontStyle && { fontStyle: attrs.fontStyle }),
            },
            'color',
            nodeFrom,
            nodeTo,
        );

        if (attrs.fontStyle === 'italic') {
            chain = chain.setTextSelection({ from: nodeFrom, to: nodeTo }).setItalic();
        }
    });

    chain.setTextSelection({ from, to }).run();
}
